import { useState } from "react";
import { searchApi, type SearchListing } from "../../api/catalogue";
import { QUICK_ORDER_MAX_LINES, type QuickOrderLine, type QuickOrderOutcome, type QuickOrderProps } from "../../components/catalogue/home/QuickOrder";
import { errorMessage } from "../account/session";
import { useAddToCart } from "../cart/useCart";

const blank = (): QuickOrderLine => ({ number: "", quantity: "", outcome: null });

/** An empty quantity means one. */
const quantityOf = (line: QuickOrderLine) => Math.max(1, Number(line.quantity) || 1);

/**
 * Quick order on the home page (KALI-31). Each number is looked up the way search looks it up,
 * and only a number that names one part in stock goes straight in. A standard asks which size,
 * an old number offers its replacement, and nothing is guessed. Lines go into the basket one at
 * a time, so a visitor's first add creates one basket, not several.
 */
export function useQuickOrder(): QuickOrderProps {
  const addToCart = useAddToCart();
  const [lines, setLines] = useState<QuickOrderLine[]>([blank(), blank(), blank()]);
  const [busy, setBusy] = useState(false);

  const setOutcome = (index: number, outcome: QuickOrderOutcome) =>
    setLines((current) => current.map((line, i) => (i === index ? { ...line, outcome } : line)));

  const add = async (listing: SearchListing, quantity: number): Promise<QuickOrderOutcome> => {
    try {
      await addToCart.mutateAsync({ listingId: listing.id, quantity });
      return { kind: "added", listing, quantity };
    } catch (error) {
      return { kind: "failed", message: errorMessage(error as Error) ?? "That didn't go into the basket. Try again." };
    }
  };

  const resolve = async (line: QuickOrderLine): Promise<QuickOrderOutcome> => {
    let found;
    try {
      found = await searchApi.search(line.number.trim());
    } catch {
      return { kind: "failed", message: "We couldn't look that number up. Check your connection and try again." };
    }
    // The start of a number, or words, isn't a part number: send them to search instead.
    if (found.match !== "number" && found.match !== "standard") return { kind: "unknown" };
    if (found.listings.length > 1) return { kind: "choose", standard: found.match === "standard", options: found.listings };
    const [listing] = found.listings;
    if (!listing.in_stock) return { kind: "out_of_stock", listing };
    if (found.replacements.length > 0) return { kind: "replaced", replacement: listing };
    return add(listing, quantityOf(line));
  };

  return {
    lines,
    busy,
    // A changed line starts over: what it said before no longer applies.
    onChange: (index, change) => setLines((current) => current.map((line, i) => (i === index ? { ...line, ...change, outcome: null } : line))),
    onAddLine: () => setLines((current) => (current.length < QUICK_ORDER_MAX_LINES ? [...current, blank()] : current)),
    onSubmit: async () => {
      setBusy(true);
      for (const [index, line] of lines.entries()) {
        if (line.number.trim() && line.outcome?.kind !== "added") setOutcome(index, await resolve(line));
      }
      setBusy(false);
    },
    onPick: async (index, listing) => {
      setBusy(true);
      setOutcome(index, await add(listing, quantityOf(lines[index])));
      setBusy(false);
    },
  };
}
